import { useState, useEffect } from 'react';
import { initializeGA, trackCookieConsent } from '../utils/analytics';

const COOKIE_CONSENT_KEY = 'datenite-cookie-consent';
const COOKIE_PREFERENCES_KEY = 'datenite-cookie-preferences';

export const useCookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
    functional: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    
    // Show banner if:
    // 1. No consent given yet (first visit)
    // 2. User previously rejected cookies (give them chance to change mind)
    if (!consent || consent === 'rejected') {
      setShowBanner(true);
    }
    
    // Only hide banner permanently if user accepted all cookies
    if (consent === 'accepted') {
      setShowBanner(false);
    }
    
    if (savedPreferences) {
      try {
        setPreferences(JSON.parse(savedPreferences));
      } catch (error) {
        console.error('Error parsing cookie preferences:', error);
      }
    }
  }, []);

  const acceptAll = () => {
    // Accept All: Enables ALL cookie types including analytics, marketing, and functional
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    };
    
    setPreferences(allAccepted);
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(allAccepted));
    setShowBanner(false);
    
    // Initialize all tracking and marketing scripts
    initializeScripts(allAccepted);
    
    // Track acceptance in Google Analytics
    trackCookieConsent('Accept All Cookies', true);
    
    console.log('✅ Accept All: All cookies enabled - Analytics, Marketing, and Functional scripts will be loaded');
  };

  const rejectAll = () => {
    // Reject All: Only keeps necessary cookies, blocks all tracking and marketing
    // Banner will show again on next visit to give user chance to change mind
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
    };
    
    // Track rejection before disabling analytics
    trackCookieConsent('Reject All Cookies', false);
    
    setPreferences(onlyNecessary);
    localStorage.setItem(COOKIE_CONSENT_KEY, 'rejected');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(onlyNecessary));
    setShowBanner(false);
    
    console.log('❌ Reject All: Only necessary cookies enabled - Banner will show again on next visit');
  };

  const initializeScripts = (prefs) => {
    // Initialize Google Analytics if analytics cookies are accepted
    if (prefs.analytics) {
      // Initialize Google Analytics
      initializeGA();
      console.log('Analytics cookies accepted - initializing tracking');
    }
    
    // Initialize marketing scripts if marketing cookies are accepted
    if (prefs.marketing) {
      // Add your marketing scripts here (Facebook Pixel, etc.)
      console.log('Marketing cookies accepted - initializing marketing scripts');
    }
    
    // Initialize functional scripts if functional cookies are accepted
    if (prefs.functional) {
      // Add your functional scripts here (chat widgets, etc.)
      console.log('Functional cookies accepted - initializing functional scripts');
    }
  };


  return {
    showBanner,
    preferences,
    acceptAll,
    rejectAll,
  };
};
